import { EXECUTIVE_ASK_SOHO } from './executiveDashboardMock'

export const DEFAULT_EXECUTIVE_FILTERS = {
  period: '1m',
  customDateRange: null,
  brandIds: ['all'],
  hotelBrandIds: ['all'],
  propertyIds: ['all'],
  comparePeriod: 'mom',
}

export const isAllFilterIds = (ids) => !ids?.length || ids.includes('all')

export const normalizeFilterIds = (ids) => {
  const list = typeof ids === 'string' ? [ids] : ids
  if (!Array.isArray(list) || list.length === 0 || list.includes('all')) return ['all']
  return [...new Set(list.filter((id) => id && id !== 'all'))]
}

export const filterIdsEqual = (left, right) => {
  const a = normalizeFilterIds(left)
  const b = normalizeFilterIds(right)
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}

export const formatMultiSelectLabel = (ids, options, allLabel, pluralNoun) => {
  const normalized = normalizeFilterIds(ids)
  if (isAllFilterIds(normalized)) return allLabel
  const labels = normalized
    .map((id) => options.find((option) => option.id === id)?.label)
    .filter(Boolean)
  if (labels.length === 0) return allLabel
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return labels.join(', ')
  return `${labels.length} ${pluralNoun}`
}

/** Selected brand/property filter ids for report API query params. */
export function toReportFilterIds(ids) {
  const normalized = normalizeFilterIds(ids)
  return isAllFilterIds(normalized) ? [] : normalized
}

export const toggleMultiSelectId = (currentIds, optionId, allOptionIds) => {
  if (optionId === 'all') return ['all']

  const normalized = normalizeFilterIds(currentIds)
  const selected = isAllFilterIds(normalized) ? new Set() : new Set(normalized)

  if (selected.has(optionId)) selected.delete(optionId)
  else selected.add(optionId)

  if (selected.size === 0) return ['all']
  if (allOptionIds?.length && selected.size >= allOptionIds.length) return ['all']
  return [...selected]
}

export function buildEmptyExecutiveDashboard(filters = DEFAULT_EXECUTIVE_FILTERS) {
  return {
    accountContext: {
      accountName: '',
      propertyCount: 0,
      followers: '—',
      followerGrowth: null,
    },
    brands: [],
    hotelBrands: [{ id: 'all', label: 'All brands' }],
    propertiesByBrand: {},
    periodLabel: null,
    askSoho: EXECUTIVE_ASK_SOHO,
    leadershipBrief: {
      financial: [],
      social: [
        {
          id: 'sentiment',
          label: 'Sentiment Score',
          value: '—',
          calculation:
            "The percentage of audience conversations, comments, reviews, and mentions about the property/portfolio that carry positive or neutral sentiment, as classified by SOHO's AI sentiment engine.",
        },
        {
          id: 'share-of-voice',
          label: 'Share of Voice',
          value: '—',
          calculation:
            "The percentage of total social conversation volume within the property's competitive set that is about the property/portfolio.",
        },
        {
          id: 'engagement-rate',
          label: 'Engagement Rate',
          value: '—',
          calculation:
            'Engagement rate measures how actively people interact with social content—engagements (likes, comments, shares, saves) divided by impressions—aggregated across the selected scope.',
        },
      ],
    },
    demandEngagement: {
      trend: [],
      demandIndex: {
        value: null,
        deltaValue: null,
        deltaDirection: 'neutral',
        deltaLabel: '',
      },
      engagementRate: {
        value: '—',
        deltaValue: null,
        deltaDirection: 'neutral',
        deltaLabel: '',
      },
      compare: { show: false },
      portfolioCoverage: { show: false },
      comparableCohort: { show: false },
      scope: { mode: 'portfolio', showPortfolioBreadth: true },
    },
    supportingInsights: {
      inboxHealth: { metrics: [] },
      propertyOutliers: null,
    },
    leadershipBriefDecisions: [],
    comparePeriod: filters.comparePeriod,
  }
}
